import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { createHub, editHub, getHub, getHubMeta } from "@/api";
import { useApp } from "@/App";
import { useHubSession, errMsg } from "@/hooks/useHubSession";
import { ImageUpload } from "@/components/hubs/ImageUpload";

const EMPTY = { name: "", tagline: "", avatar: "", banner: "", tags: [], rules: "", links: { x: "", discord: "", telegram: "" } };
const field = "w-full bg-[#12131C] border border-[#2A2E45] rounded-xl px-4 py-3 outline-none focus:border-orange-500/60 transition-colors";

export default function HubStudioPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { showToast } = useApp();
  const { address, withSession } = useHubSession();
  const [f, setF] = useState(EMPTY);
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getHubMeta().then((m) => setTags(m.tags)); }, []);
  useEffect(() => {
    if (!slug) return;
    getHub(slug, address || "").then((h) => setF({ ...EMPTY, ...h, links: { ...EMPTY.links, ...h.links } }));
  }, [slug, address]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleTag = (t) => set("tags", f.tags.includes(t) ? f.tags.filter((x) => x !== t) : f.tags.length < 3 ? [...f.tags, t] : f.tags);

  const save = async (e) => {
    e.preventDefault();
    if (!address) return showToast("Connect your wallet first", "error");
    setSaving(true);
    const body = { name: f.name, tagline: f.tagline, avatar: f.avatar, banner: f.banner, tags: f.tags, rules: f.rules, links: f.links };
    try {
      const h = await withSession((t) => (slug ? editHub(t, slug, body) : createHub(t, body)));
      showToast(slug ? "Hub updated" : "Your hub is live!");
      nav(`/hubs/${h.slug}`);
    } catch (err) { showToast(errMsg(err), "error"); }
    setSaving(false);
  };

  const up = { withSession, showToast };
  return (
    <div className="pt-24 pb-20" data-testid="hub-studio-page">
      <form onSubmit={save} className="max-w-3xl mx-auto px-4 space-y-6">
        <div>
          <div className="chip inline-flex text-amber-300 border-amber-400/40"><Sparkles size={12} /> Creator Hub Studio</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-3">{slug ? "Edit your hub" : "Launch a Flame Hub"}</h1>
          <p className="text-slate-400 mt-2">Members count toward milestones once their wallet has burned 5+ CRO and is 7+ days old.</p>
        </div>
        <ImageUpload label="Banner image" value={f.banner} onChange={(v) => set("banner", v)} aspect="h-40" tid="studio-banner-upload" {...up} />
        <div className="grid sm:grid-cols-[140px_1fr] gap-5">
          <ImageUpload label="Avatar" value={f.avatar} onChange={(v) => set("avatar", v)} aspect="h-32" tid="studio-avatar-upload" {...up} />
          <div className="space-y-4">
            <input className={field} placeholder="Hub name" value={f.name} maxLength={40} onChange={(e) => set("name", e.target.value)} data-testid="studio-name-input" required minLength={3} />
            <input className={field} placeholder="Tagline" value={f.tagline} maxLength={100} onChange={(e) => set("tagline", e.target.value)} data-testid="studio-tagline-input" />
          </div>
        </div>
        <div>
          <div className="text-sm text-slate-300 mb-2">Topics (pick 1–3)</div>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <button type="button" key={t} onClick={() => toggleTag(t)} data-testid={`studio-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`chip transition-colors ${f.tags.includes(t) ? "border-orange-500 text-orange-300 bg-orange-500/10" : ""}`}>{t}</button>
            ))}
          </div>
        </div>
        <textarea className={`${field} h-32`} placeholder="Community rules" value={f.rules} maxLength={2000} onChange={(e) => set("rules", e.target.value)} data-testid="studio-rules-input" />
        <div className="grid sm:grid-cols-3 gap-3">
          {[["x", "Twitter / X"], ["discord", "Discord"], ["telegram", "Telegram"]].map(([k, l]) => (
            <input key={k} className={field} placeholder={`${l} (https://…)`} value={f.links[k] || ""} data-testid={`studio-link-${k}`}
              onChange={(e) => set("links", { ...f.links, [k]: e.target.value })} />
          ))}
        </div>
        <button type="submit" disabled={saving} className="btn-tactile btn-flame w-full font-display text-lg" data-testid="studio-submit-button">
          {saving ? "Saving…" : slug ? "Save changes" : "Launch hub"}
        </button>
      </form>
    </div>
  );
}
